import React, {useEffect, useMemo, useState} from "react";
import Forms from "../../../bricks/form/Forms";
import {languageOptions as defaultLanguageOptions} from "../../../config/Config";

import "./HelpUploadModalContent.scss";

/**
 * Modál pro vytvoření / editaci souboru s variantami pro jednotlivé jazyky.
 *
 * Props:
 * - mode: "create" | "edit"
 * - languageOptions?: { [code]: label }
 * - initial?: {
 *     _id?: string,
 *     label?: string,
 *     filenameLocale?: { [locale: string]: { filename?: string, locale: string, description?: string } }
 *   }
 * - onSubmit: async ({ label, filesByLang, descriptionsByLang }) => void
 * - close?  (poskytuje ModalContainer)
 */
const HelpUploadModalContent = ({
                                    mode = "create",
                                    languageOptions = defaultLanguageOptions,
                                    initial = {},
                                    onSubmit,
                                    close
                                }) => {
    const langCodes = useMemo(() => Object.keys(languageOptions || {}), [languageOptions]);

    const [label, setLabel] = useState(initial.label || "");

    // File objekty (jen to, co uživatel vybere; při editaci prázdné = nic neměnit)
    const [filesByLang, setFilesByLang] = useState(() => {
        const m = {};
        for (const lng of langCodes) m[lng] = null;
        return m;
    });

    // Popisy/poznámky k souboru dle jazyka
    const [descriptionsByLang, setDescriptionsByLang] = useState(() => {
        const base = {};
        for (const lng of langCodes) {
            base[lng] = initial?.filenameLocale?.[lng]?.locale || "";
        }
        return base;
    });

    const filenameLocale = initial.filenameLocale || {};

    const handleFileChange = (lng, ...args) => {
        // kompatibilně uchopíme File z různých signatur
        const candidate = args[1] ?? args[0];
        let file = null;

        if (candidate instanceof File) {
            file = candidate;
        } else if (candidate?.target?.files?.[0]) {
            file = candidate.target.files[0];
        }

        setFilesByLang((prev) => ({...prev, [lng]: file || null}));
    };

    const handleDescriptionChange = (lng, value) => {
        setDescriptionsByLang((prev) => ({...prev, [lng]: value}));
    };

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        await onSubmit({
            label: label?.trim(),
            filesByLang,
            descriptionsByLang
        });
        close?.(true);
    };

    return (
        <div className="HelpUploadModalContent">
            <form onSubmit={handleSubmit}>
                <div className="row">
                    <Forms.Text
                        name="label"
                        label="Název / štítek sady souborů"
                        value={label}
                        onChange={(_, val) => setLabel(val)}
                        required
                    />
                </div>

                <div className="grid">
                    {langCodes.map((lng) => {
                        const pretty = languageOptions[lng] || lng.toUpperCase();
                        const existing = filenameLocale?.[lng]?.filename;

                        return (
                            <div key={lng} className="lang-block">
                                <h4>
                                    {pretty} <small>({lng.toUpperCase()})</small>
                                </h4>

                                {mode === "edit" && (
                                    <div className="field">
                                        <label>Stávající soubor</label>
                                        <div className="current-file">
                                            {existing ? <>✓ {existing}</> : <span className="muted">— žádný —</span>}
                                        </div>
                                    </div>
                                )}

                                <div className="field">
                                    <Forms.File
                                        name={`file_${lng}`}
                                        accept="*/*"
                                        onChange={(...args) => handleFileChange(lng, ...args)}
                                    />
                                    {mode === "edit" && (
                                        <small className="muted">
                                            Pokud nevybereš soubor, varianta pro {pretty} zůstane beze změny.
                                        </small>
                                    )}
                                </div>

                                <Forms.Text
                                    value={descriptionsByLang[lng]}
                                    onChange={(_, val) => handleDescriptionChange(lng, val)}
                                    placeholder={`Popis pro ${pretty}`}
                                />
                            </div>
                        );
                    })}
                </div>

                <div className="actions">
                    <Forms.Button type="submit" label={mode === "edit" ? "Uložit změny" : "Nahrát"}/>
                    &nbsp;
                    <Forms.Button type="button" variant="secondary" label="Zavřít" onClick={() => close?.()}/>
                </div>
            </form>
        </div>
    );
};

export default HelpUploadModalContent;
